<script lang="tsx">
  import NewBsBox from '@xhs/yam-beer/cmp/primitive/NewBsBox'
  import {
    dayjs,
    getDayMatrix,
    isDayEqual,
    isDayRange,
  } from '@xhs/yam-beer/utils/dayjs'
  import type { Dayjs } from 'dayjs'
  import { ifExistCall } from '@xhs/yam-beer/utils/functionUtils'
  import { ViewModeType } from '@xhs/yam-beer/cmp/constants'

  export type CalanderDayViewProps = {
  viewMode: ViewModeType
  current: Dayjs
  startDay: Dayjs
  endDay: Dayjs
  selected: Dayjs[]
  disableDay?: (d: Dayjs, s: Dayjs, e: Dayjs) => boolean
  onClickDay?: (d: Dayjs) => void
  handleMouseOver?: (d: Dayjs) => void
}

  function isIncludeDay(current: Dayjs, day: string, selected: Dayjs[]) {
    if (selected === undefined) {
      return false
    }
    return selected.some(v => isDayEqual(dayjs(current).date(parseInt(day, 10)), v))
  }

  function isOutside(date: string, rowIndex: number, lastRowIndex: number) {
    const dateInt = parseInt(date, 10)

    const isLastMonth = (rowIndex === 0) && (dateInt > 7)
    const isNextMonth = (rowIndex === lastRowIndex) && (dateInt < 7)
    return isLastMonth || isNextMonth
  }

type DayClassProps = Pick<
  CalanderDayViewProps, 'current'|'startDay'|'endDay'|'selected'|'disableDay'
>

  function getDayClass(
    props: DayClassProps, date: string, rowIndex: number, lastRowIndex: number,
  ) {
    const {
      current, startDay, endDay, selected, disableDay,
    } = props
    const currentDate = dayjs(current).date(parseInt(date, 10))

    if (!date.trim()) {
      return {}
    }
    if (isOutside(date, rowIndex, lastRowIndex)) {
      return {
        'data-outside': true,
      }
    }
    const cls = {
      'data-end': isDayEqual(currentDate, endDay) || null,
      'data-range': isDayRange(currentDate, startDay, endDay) || null,
      'data-selected': isIncludeDay(current, date, selected) || null,
      'data-disabled': disableDay
        ? (disableDay(
          currentDate.hour(0).minute(0).second(0) || null,
          startDay,
          endDay,
        ) || null)
        : null,
      'data-start': isDayEqual(currentDate, startDay) || null,
      'data-today': isDayEqual(currentDate, dayjs()) || null,
    }
    return cls
  }

type ClickProps = Pick<
  CalanderDayViewProps, 'current'|'startDay'|'endDay'|'onClickDay'|'disableDay'
>

  function handleClick(
    props: ClickProps,
    date: string,
    rowIndex: number,
    lastRowIndex: number,
  ) {
    const {
      current, disableDay, startDay, endDay,
    } = props
    let currentDate = dayjs(current).date(parseInt(date, 10))
    /** last month day */
    if (isOutside(date, rowIndex, lastRowIndex)) {
      if (rowIndex === 0) {
        currentDate = dayjs(current).subtract(1, 'month').date(parseInt(date, 10))
      } else if (rowIndex === lastRowIndex) {
        /** next month day */
        currentDate = dayjs(current).add(1, 'month').date(parseInt(date, 10))
      }
    }
    if (!(disableDay?.(currentDate.hour(0).minute(0).second(0), startDay, endDay))) {
      ifExistCall(props.onClickDay, currentDate)
    }
  }

type MouseOverProps = Pick<
  CalanderDayViewProps, 'current'|'startDay'|'endDay'|'handleMouseOver'|'disableDay'
>

  function handleMouseOver(
    props: MouseOverProps,
    date: string, rowIndex: number, lastRowIndex: number,
  ) {
    const {
      startDay,
      endDay,
      current,
      disableDay,
      handleMouseOver: fn,
    } = props
    const currentDate = dayjs(current).date(parseInt(date, 10))
    const disabled = disableDay?.(currentDate.hour(0).minute(0).second(0), startDay, endDay)
    if (isOutside(date, rowIndex, lastRowIndex)) {
      return
    }
    // fix: range when disabled can't be selected
    ifExistCall(fn, dayjs(current).date(parseInt(date, 10)), { disabled })
  }

  const CalanderDayView = (props: CalanderDayViewProps) : JSX.Element => {
    const { current } = props
    const dayMatrix = getDayMatrix(
      dayjs(current).year(),
      dayjs(current).month(),
    )
    const lastRowIndex = dayMatrix.length - 1
    return (
    <NewBsBox>
      {dayMatrix.map((row, rowIndex) => (
        <NewBsBox key={rowIndex} bs={{ 'cmp.calendar': 'row' }}>
          {row.map((item: string) => (
            <NewBsBox
              bs={{ 'cmp.calendar': 'dayCell' }}
              {...{
                ...(getDayClass(
                  props,
                  item,
                  rowIndex,
                  lastRowIndex,
                )),
                onClick: () => handleClick(props, item, rowIndex, lastRowIndex),
                onMouseover: () => handleMouseOver(props, item, rowIndex, lastRowIndex),
              }}
              key={item}
            >
              {item}
            </NewBsBox>
          ))}
        </NewBsBox>
      ))}
    </NewBsBox>
  )
  }

  export default CalanderDayView
</script>
